<template>
  <router-view v-slot="{ Component, route }">
    <keep-alive>
      <component
        :key="route.name"
        :is="Component"
        v-if="!route?.meta?.isIframe"
      />
    </keep-alive>
  </router-view>
  <component
    v-show="item.name === iframeInfo.routeName"
    :key="item.name"
    :is="EmbedIframe"
    v-for="item in iframeInfo.iframeList"
    :iframeUrl="item.meta.iframeUrl"
  />
  <page-loading></page-loading>
  <page-error-tip></page-error-tip>
</template>
<script setup lang="ts">
import PageLoading from "@components/common/PageLoading.vue";
import PageErrorTip from "@components/common/PageErrorTip.vue";
import { nowLang, defaultLang } from "@/language";
import {
  initModuleConfig,
  initRoutes,
  initJumpRouter,
} from "@config/initRouter";
import { useBaseStore } from "@store/base";
import { useArticleStore } from "@store/article";
import { getWebsiteConfig, checkLoginType } from "@api/base";
import { initColorConfig } from "@config/initColor";
import {
  initMaterialConfig,
  initLanguageConfig,
  getText,
} from "@config/siteConfig";
import useLang from "@hooks/useLang";
import { getParam } from "@utiles/index";
import useLoading from "@hooks/useLoading";
import { getSiteTag } from "@api/article";
import bridge from "@utiles/bridge";
import { router } from "@router/index";
import { updateCommonParamsInfo } from "@biPoint/commonParam";
import { iframeConfig, transformModuleConfig } from "@config/embedIframe";
import { onMounted, reactive, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import EmbedIframe from "@views/EmbedIframe.vue";

const base = useBaseStore();
const route = useRoute();
const { setLoadingState } = useLoading();

const setSiteError = () => {
  base.setPageErrorTipVisible(true);
  base.setPageErrorTipMessage(getText("siteError"));
  setLoadingState(false);
};

const iframeInfo = reactive({
  showIframe: false,
  routeName: "",
  iframeList: [],
});
watch(
  route,
  (newVal) => {
    const { name, meta } = newVal;
    if (meta?.isIframe) {
      iframeInfo.routeName = name;
      iframeInfo.showIframe = true;
    } else {
      iframeInfo.showIframe = false;
      iframeInfo.routeName = "";
    }
  },
  { immediate: true }
);

const initSiteName = () => {
  const name = getParam("name");
  if (name) {
    base.setName(name);
    updateCommonParamsInfo({
      pageId: name,
    });
  } else {
    setSiteError();
  }
};
const initPageBar = () => {
  if (bridge.runInsdk()) {
    bridge.callSdkMethod("hideNavigationView", {}, (res) => {
      if (res && +res.result === 1) {
        base.setShowPageBar(true);
      } else {
        base.setShowPageBar(false);
      }
    });
  } else {
    base.setShowPageBar(false);
  }
};
const initPreviewState = () => {
  const preview = getParam("preview");
  const sign = getParam("sign");
  if (preview == 1 && sign) {
    base.setPreview(1);
    base.setSign(sign);
  }
};
const getInitWebsiteConfig = async () => {
  const res = await getWebsiteConfig({
    name: base.name,
    language: nowLang.value,
    preview: base.preview,
    sign: base.sign,
  });
  if (res?.result == 1 && res?.data) {
    const { websiteConfig, moduleConfig, languageConfig } = res.data;
    if (websiteConfig) {
      initColorConfig(JSON.parse(websiteConfig?.WEBSITE_COLOR || "{}"));
      initMaterialConfig(JSON.parse(websiteConfig?.MATERIAL_JSON || "{}"));
      initLanguageConfig(JSON.parse(websiteConfig?.LANGUAGE_JSON || "{}"));
      base.setGameId(websiteConfig?.GAME_ID || "");
      updateCommonParamsInfo({
        roleId:
          getParam("roleId") || getParam("role_id") || getParam("roleID") || "",
        roleName: getParam("roleName") || getParam("role_name") || "",
        serverId:
          getParam("serverId") ||
          getParam("server_id") ||
          getParam("serverID") ||
          "",
        gameId: websiteConfig?.GAME_ID || "",
      });
    }
    if (
      languageConfig &&
      Array.isArray(languageConfig) &&
      languageConfig.length > 0
    ) {
      const { changeLang } = useLang();
      if (languageConfig.includes(nowLang.value)) {
        base.setLang(nowLang.value);
        updateCommonParamsInfo({
          language: nowLang.value,
        });
      } else {
        changeLang(languageConfig[0]);
        updateCommonParamsInfo({
          language: languageConfig[0],
        });
      }
      base.setDefaultLang(languageConfig[0]);
      defaultLang.value = languageConfig[0];
    }
    if (moduleConfig) {
      if (iframeConfig[base.name]) {
        transformModuleConfig(moduleConfig, base.name);
      }
      initModuleConfig(moduleConfig);
      base.setModuleConfig(moduleConfig);
      initRoutes(router, moduleConfig);
      iframeInfo.iframeList = router.getRoutes().filter((item) => {
        return item.meta?.isIframe;
      });
      initJumpRouter(router, moduleConfig);
    }
  } else {
    if (res?.result == 21000) {
      setSiteError();
    }
  }
};
const initTagList = async () => {
  const article = useArticleStore();
  const res = await getSiteTag({
    name: base.name,
    preview: base.preview,
    sign: base.sign,
  });
  if (res?.result == 1 && res?.data?.tagList) {
    const newTagList = res.data.tagList.map((item) => {
      return {
        CONTENT: JSON.parse(item.CONTENT || "{}"),
        ID: item.ID,
        NAME: item.NAME,
      };
    });
    article.setTagList(newTagList);
  }
};
const initLoginData = () => {
  checkLoginType().then((res) => {
    if (res?.result == 1) {
      updateCommonParamsInfo({
        loginAccount: res.data.LOGIN_ACCOUNT,
        uid: res.data.UID,
      });
      window.officialGuideDataLayer.push({
        eventName: "login",
        eventType: res?.data?.LOGIN_PATH || "",
      });
    }
  });
};
const initSiteData = async () => {
  try {
    setLoadingState(true);
    initSiteName();
    initPageBar();
    initPreviewState();
    await getInitWebsiteConfig();
    initLoginData();
    await initTagList();
  } catch (error) {
    console.log(error);
  }
};
initSiteData();
</script>
